import React, { useEffect, useState } from "react";
import { CustomerMeResponse, getUserData } from "services/indexcServices";
import { useMediaQuery } from "react-responsive";

const INDECX_WIDGET_KEY = "5e46926028a0ef00ed5fc759";
const INDECX_WIDGET_ACTION = "61794f29945586195bd57ab7";

declare global {
  interface Window {
    // eslint-disable-next-line no-underscore-dangle
    __INDECX_WIDGET_LOADED__?: boolean;
  }
}

// eslint-disable-next-line no-underscore-dangle
if (typeof window !== "undefined" && !window.__INDECX_WIDGET_LOADED__) {
  // eslint-disable-next-line no-underscore-dangle
  window.__INDECX_WIDGET_LOADED__ = false;
}

const PesquisaIndecx: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 620 });
  const [userData, setUserData] = useState<CustomerMeResponse | null>(null);

  useEffect(() => {
    const fetchUserData = async (): Promise<void> => {
      const data = await getUserData();
      if (data) {
        setUserData(data);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const scriptId = "indecx-widget-js";
    const widgetContainerId = "indecx-widget-container";
    const widgetMinimizedId = "indecx-widget-minimized";

    const adjustWidgetPosition = (): void => {
      const widget = document.getElementById(widgetContainerId);
      const widgetMinimized = document.getElementById(widgetMinimizedId);
      const right = isMobile ? "100px" : "150px";

      if (widget) {
        widget.style.setProperty("right", right);
      }

      if (widgetMinimized) {
        widgetMinimized.style.setProperty("right", right, "important");
      }
    };

    const cleanup = (): void => {
      // eslint-disable-next-line no-underscore-dangle
      window.__INDECX_WIDGET_LOADED__ = false;

      const scriptEl = document.getElementById(scriptId);
      if (scriptEl) scriptEl.remove();

      const widgetContainer = document.getElementById(widgetContainerId);
      if (widgetContainer) widgetContainer.remove();

      const widgetMin = document.getElementById(widgetMinimizedId);
      if (widgetMin) widgetMin.remove();
    };

    const buildConfig = (): {
      key: string;
      action: string;
      name: string;
      email: string;
      phone: string;
      indicators: {
        cpf_cnpj: string;
        codCliente: string;
        codEndereco: string;
        estado: string;
        cidade: string;
      };
    } => {
      // Buscar dados do primeiro endereço ativo ou usar fallback do localStorage
      const firstAddress = userData?.customerAddresses?.[0];
      const phoneContact = firstAddress?.contacts?.find(
        (c) => c.contactType === "CELL" || c.contactType === "PHONE"
      );
      const emailContact = firstAddress?.contacts?.find(
        (c) => c.contactType === "EMAIL"
      );

      return {
        key: INDECX_WIDGET_KEY,
        action: INDECX_WIDGET_ACTION,
        name:
          userData?.corporateName ||
          localStorage.getItem("@UltragazEmpresarial:customerName") ||
          "",
        email:
          userData?.email ||
          emailContact?.contact ||
          localStorage.getItem("@UltragazEmpresarial:loginEmail") ||
          "",
        phone:
          phoneContact?.contact ||
          (userData?.phoneDdd && userData?.phoneNumber
            ? `${userData.phoneDdd}${userData.phoneNumber}`
            : "") ||
          localStorage.getItem("@UltragazEmpresarial:phone") ||
          "",
        indicators: {
          cpf_cnpj:
            userData?.document ||
            localStorage.getItem("@UltragazEmpresarial:document") ||
            "CPF_INFO",
          codCliente:
            userData?.customerCode ||
            localStorage.getItem("@UltragazEmpresarial:customerCode") ||
            "COD_CLIENTE",
          codEndereco:
            userData?.customerSiteId ||
            firstAddress?.siteId ||
            localStorage.getItem("@UltragazEmpresarial:customerSiteId") ||
            "COD_ENDERECO",
          estado:
            firstAddress?.stateCode ||
            localStorage.getItem("@UltragazEmpresarial:estado") ||
            "ESTADO",
          cidade:
            firstAddress?.city ||
            localStorage.getItem("@UltragazEmpresarial:cidade") ||
            "CIDADE",
        },
      };
    };

    const loadWidget = (): void => {
      // Se já está carregado, apenas ajusta a posição
      // eslint-disable-next-line no-underscore-dangle
      if (window.__INDECX_WIDGET_LOADED__) {
        adjustWidgetPosition();
        return;
      }

      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();

      cleanup();

      const config = buildConfig();

      const script = document.createElement("script");
      script.id = scriptId;
      script.src =
        "https://indecx-widget.s3.amazonaws.com/widget-production-v3.js";
      script.type = "text/javascript";
      script.async = true;

      script.onload = () => {
        // eslint-disable-next-line no-underscore-dangle
        if (window.__INDECX_WIDGET_LOADED__) return;

        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { IndeCX } = window as any;
          if (!IndeCX) {
            return;
          }

          IndeCX(config);
          // eslint-disable-next-line no-underscore-dangle
          window.__INDECX_WIDGET_LOADED__ = true;

          setTimeout(() => {
            adjustWidgetPosition();
          }, 500);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Erro ao inicializar IndeCX widget:", error);
          // eslint-disable-next-line no-underscore-dangle
          window.__INDECX_WIDGET_LOADED__ = false;
        }
      };

      document.body.appendChild(script);
    };

    if (userData !== null) {
      loadWidget();
    }

    return cleanup;
  }, [isMobile, userData]);

  return null;
};

export { PesquisaIndecx };
